import React, { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

/**
 * Premium magnetic-style button wrapper. The child element (typically a
 * <button> or <a>) is gently pulled toward the cursor while hovered.
 * Use sparingly on primary CTAs — that's where it earns its keep.
 *
 * Render exactly one child. The child's positioning + styling is left
 * untouched; we only apply transform on a wrapping span.
 */
export const Magnetic = ({
  children,
  strength = 0.35,
  className = "",
  as = "span",
  ...rest
}) => {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 260, damping: 22, mass: 0.4 });
  const sy = useSpring(y, { stiffness: 260, damping: 22, mass: 0.4 });

  const onMove = (e) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    x.set((e.clientX - cx) * strength);
    y.set((e.clientY - cy) * strength);
  };

  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  const Comp = as === "div" ? motion.div : motion.span;

  return (
    <Comp
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ x: sx, y: sy, display: "inline-flex" }}
      className={className}
      {...rest}
    >
      {children}
    </Comp>
  );
};
